/* eslint-disable camelcase */
/* eslint-disable complexity */
import React, {useCallback, useState, useRef, useEffect, Key} from 'react';
import {Carousel} from '@baidu/hcui';
import cx from 'classnames';
import {TopicItemInfo, TopicModule} from '@/services/pages/channel/channel.client';
import Title from '@/components/common/title';
import useJumpUrl from '@/hooks/useJumpUrl';
import {play as logPlayReport} from '@/utils/ubcUtils';
import {VALUE_TYPE} from '@/dicts/consts';
import {getUrlParamValue} from '@/utils/utils';
import {baseSendExposure} from '@/utils/logger/baseSendExposure';
import * as domUtil from '@/utils/dom';
import styles from './index.less';

interface Props {
    page: string;
    sfRef?: string;
    bannersModel: TopicModule;
    channelBusinessLine?: string;
    ubcValue?: string;
}

const VideoSwpier = ({bannersModel, page, ubcValue = '', channelBusinessLine}: Props) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [playVideo, setPlayVideo] = useState(false);
    const [autoPlay, setAutoPlay] = useState(true);
    const [currentvideoIndex, setCurrentVideoIndex] = useState(0);
    const carsoul = useRef();
    const fromRef = useRef('');
    const isPlayValid = useRef(false);
    const isPlayDone = useRef(false);
    const isExposure = useRef(false);
    const containerSize = useRef({top: 0, bottom: 0});

    const videoIdList = useRef(new Map<Key, number>());
    const collectIds = useCallback(
        () => {
            if (
                bannersModel
                && bannersModel.itemList
                && bannersModel.itemList.length
            ) {
                bannersModel.itemList.map(
                    banner => {
                        videoIdList.current.set(banner.itemId, 0);
                        return null;
                    }
                );
            }
        },
        [bannersModel]
    );

    useEffect(
        () => {
            videoIdList.current = new Map();
            containerSize.current = domUtil.getViewport();
            collectIds();
        },
        [collectIds]
    );

    // 避免同一页面出现多个视频模块 导致index混乱
    const videoSwiperId = bannersModel.moduleId?.toString() ?? '';
    const handleScroll = useCallback(
        () => {
            const videoContainer = document.getElementById(`${videoSwiperId}`) as HTMLVideoElement;
            isExposure.current = domUtil.isInViewport(videoContainer, containerSize.current, 0);

        },
        [videoSwiperId]
    );

    useEffect(
        () => {
            window.addEventListener('scroll', handleScroll, false);
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        },
        [handleScroll]
    );

    useEffect(
        () => {
            handleScroll();
            if (
                isExposure.current
                && bannersModel
                && bannersModel.itemList
                && bannersModel.itemList.length === 1
                && channelBusinessLine
            ) {
                baseSendExposure({
                    'page': page,
                    'value': ubcValue,
                    'pageSourceId': bannersModel.channelId,
                    'pageType': channelBusinessLine,
                    'valueId': bannersModel.itemList[0].itemId,
                    'valueTitle': bannersModel.itemList[0].title,
                    'valueType': VALUE_TYPE.ITEM,
                });
            }
        },
        [bannersModel, channelBusinessLine, handleScroll, page, ubcValue, videoSwiperId]
    );

    const itemNumber = bannersModel.itemList ? bannersModel.itemList.length : 0;
    // 切换时自动暂停视频、且隐藏前一个视频、且显示下一个视频封面和播放icon
    const handleTransitionStart = useCallback(
        (index: number) => {
            setCurrentVideoIndex(index);
            if (playVideo) {
                setPlayVideo(false);
                // eslint-disable-next-line max-len
                const curVideoEl = document.getElementById(`v-${currentvideoIndex}-${videoSwiperId}`) as HTMLVideoElement;
                if (curVideoEl) {
                    curVideoEl.pause();
                }
            }
            const banner = bannersModel && bannersModel.itemList && bannersModel?.itemList[index];

            if (
                banner
                && videoIdList.current.get(banner.itemId) === 0
                && isExposure.current
            ) {
                videoIdList.current.set(banner.itemId, 1);
                baseSendExposure({
                    'page': page,
                    'value': ubcValue,
                    'pageSourceId': bannersModel.channelId,
                    'pageType': channelBusinessLine,
                    'valueId': banner.itemId,
                    'valueTitle': banner.title,
                    'valueType': VALUE_TYPE.ITEM,
                });
            }
        },
        [bannersModel, channelBusinessLine, currentvideoIndex, page, playVideo, ubcValue, videoSwiperId]
    );

    const {navigateToUrl} = useJumpUrl();

    const handleBannerClick = useCallback(
        (linkUrl?: string, swanUrl?: string) => {
            const urlParams = {
                linkUrl,
                swanUrl,
            };
            const logParams = {
                page: page,
                value: ubcValue,
                channelId: bannersModel?.channelId,
                pageType: channelBusinessLine,
                moduleId: bannersModel?.moduleId,
                moduleName: bannersModel?.moduleName,
            };
            navigateToUrl(urlParams, logParams);
        },
        [
            bannersModel.channelId,
            bannersModel.moduleId,
            bannersModel.moduleName,
            channelBusinessLine,
            page,
            ubcValue,
            navigateToUrl,
        ]
    );

    const isVideoLink = (url: string) => {
        const index1 = url.lastIndexOf('.');
        const index2 = url.length;
        const suffix = url.substring(index1, index2);
        return suffix === '.mp4';
    };

    const initPlayLog = useCallback(
        (value: string) => {
            if (!bannersModel?.itemList?.length) {
                return;
            }
            const currentVideo = bannersModel?.itemList
                ? bannersModel?.itemList[currentvideoIndex]
                : {} as TopicItemInfo;
            logPlayReport({
                page: page,
                value: value,
                ext: {
                    sf_ref: getUrlParamValue(window.location.href, 'sf_ref'),
                    sf_ch: getUrlParamValue(window.location.href, 'sf_ch'),
                    page_type: channelBusinessLine,
                    in_ref_from: getUrlParamValue(window.location.href, 'from'),
                    value_id: currentVideo.itemId,
                    value_title: currentVideo.title,
                    value_type: VALUE_TYPE.ITEM,
                },
            });
        },
        [bannersModel?.itemList, channelBusinessLine, currentvideoIndex, page]
    );

    const playEndedHandler = useCallback(
        () => {
            carsoul?.current?.startAutoPlay();
            setPlayVideo(false);
        },
        []
    );

    const onTimeUpdate = useCallback(
        (event: React.FormEvent) => {
            if (!event) {
                return;
            }

            // 播放时间大于10s打点
            if (
                !isPlayValid.current
                && event.target
                && event.target?.currentTime > 10
            ) {
                isPlayValid.current = true;
                initPlayLog('play_valid');
            }
            // 播放时间大于80%打点
            else if (
                !isPlayDone.current
                && event.target
                && Number((event.target?.currentTime / event.target?.duration)) * 100 > 80
            ) {
                isPlayDone.current = true;
                initPlayLog('play_done');
            }
        },
        [initPlayLog]
    );

    const onPosterClk = useCallback(
        (e: React.FormEvent, banner, index: number) => {
            e.preventDefault();
            e.stopPropagation();
            setCurrentVideoIndex(index);
            setPlayVideo(true);

            // 播放play打点
            logPlayReport({
                page: page,
                value: 'play',
                ext: {
                    sf_ref: getUrlParamValue(window.location.href, 'sf_ref'),
                    sf_ch: getUrlParamValue(window.location.href, 'sf_ch'),
                    page_type: channelBusinessLine,
                    in_ref_from: getUrlParamValue(window.location.href, 'from'),
                    value_id: banner.itemId,
                    value_title: banner.title,
                    value_type: VALUE_TYPE.ITEM,
                },
            });
            setAutoPlay(false);
            carsoul?.current?.pauseAutoPlay();
            // 由于轮播图组件设计-最后一个元素会被复制一份，所以暂时在此处做一个处理
            if (index === itemNumber - 1) {
                // eslint-disable-next-line max-len
                document.querySelectorAll(`#v-${index}-${videoSwiperId}`)[1]?.play();
            }
            document.getElementById(`v-${index}-${videoSwiperId}`)?.play();
        },
        [channelBusinessLine, itemNumber, page, videoSwiperId]
    );

    useEffect(
        () => {
            const targetUrlObj = new URL(window.location.href);
            if (targetUrlObj) {
                fromRef.current = targetUrlObj.searchParams.get('from') ?? '';
            }
        },
        []
    );

    return (

        <div
            id={videoSwiperId}
            data-show={
                JSON.stringify({
                    'page': page,
                    'value': ubcValue,
                    'pageSourceId': bannersModel.channelId,
                    'pageType': channelBusinessLine,
                    'valueId': bannersModel.moduleId,
                    'valueTitle': bannersModel.moduleName,
                    'valueType': VALUE_TYPE.MODULE,
                })
            }
        >
            {
                (bannersModel.isShowTitle === 1 && (bannersModel.moduleName || bannersModel.moduleSubTitle))
                && (
                    <div className={styles.titleWrap}>
                        <Title
                            title={bannersModel.moduleName ?? ''}
                            subTitle={bannersModel.moduleSubTitle ?? ''}
                            moreLink={bannersModel.moreUrl ?? ''}
                        />
                    </div>
                )
            }


            <Carousel
                ref={carsoul}
                autoPlayIntervalTime={5000}
                moveDistanceRatio={0}
                autoPlay={autoPlay}
                loop={(bannersModel?.itemList?.length || 0) > 1}
                showPagination={(bannersModel?.itemList?.length || 0) > 1}
                style={{
                    fontSize: '0',
                }}
                // eslint-disable-next-line react/jsx-no-bind
                onChange={handleTransitionStart}
                className={styles.carouselWrap}
            >
                { bannersModel.itemList && bannersModel.itemList.map(
                    (banner, index) => {
                        const isVideo = isVideoLink(banner.fieldValue?.link ?? '');
                        return (
                            isVideo
                                ? (
                                    <div
                                        id={banner.itemId?.toString()}
                                        key={banner.itemId}
                                        className={cx(
                                            styles.bannerWrapper,
                                            {
                                                [styles.regt]: bannersModel.moduleFillet === 1,
                                            }
                                        )}
                                    >
                                        <img
                                            id={`img-${index}`}
                                            src={banner.fieldValue?.img}
                                            className={styles.imgCard}
                                            alt="swiperPng"
                                            style={{display: playVideo ? 'none' : 'block'}}
                                        />
                                        <img
                                            className={styles.playIcon}
                                            src={'https://med-fe.cdn.bcebos.com/content-external-channel/play.png'}
                                            style={{display: playVideo ? 'none' : 'block'}}
                                            onClick={e => onPosterClk(e, banner, index)}
                                        />
                                        <video
                                            ref={videoRef}
                                            id={`v-${index}-${videoSwiperId}`}
                                            controls
                                            muted
                                            preload={banner.fieldValue?.link}
                                            src={banner.fieldValue?.link ?? ''}
                                            poster={banner.fieldValue?.img}
                                            style={{display: playVideo ? 'block' : 'none'}}
                                            controlsList="nodownload"
                                            className={styles.video}
                                            playsInline
                                            // eslint-disable-next-line react/no-unknown-property
                                            webkit-playsinline="true"
                                            // eslint-disable-next-line react/no-unknown-property
                                            x5-playsinline="true"
                                            // eslint-disable-next-line react/no-unknown-property
                                            x5-video-player-fullscreen="false"
                                            // eslint-disable-next-line react/no-unknown-property
                                            x-webkit-airplay="allow"
                                            // eslint-disable-next-line react/no-unknown-property
                                            x5-video-player-type="h5"
                                            onEnded={playEndedHandler}
                                            onTimeUpdate={onTimeUpdate}
                                        />
                                    </div>
                                )
                                : (
                                    <img
                                        key={banner.itemId}
                                        onClick={() => handleBannerClick(
                                            banner.fieldValue?.link,
                                            banner.fieldValue?.swanUrl
                                        )}
                                        src={banner.fieldValue?.img}
                                        className={styles.imgCard}
                                        alt="swiperPng"
                                        style={{
                                            borderRadius: bannersModel.moduleFillet === 1 ? '0' : '12px',
                                        }}
                                    />
                                )
                        );
                    }
                )
                }
            </Carousel>

        </div>

    );
};

export default VideoSwpier;
